import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Check } from 'lucide-react';
import './VideoTrimmer.css';

const VideoTrimmer = ({ videoFile, onTrimComplete, onCancel }) => {
  const [videoDuration, setVideoDuration] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(30);
  const [isPlaying, setIsPlaying] = useState(false);
  
  const videoRef = useRef(null);

  // Load video metadata
  useEffect(() => {
    if (videoFile && videoRef.current) {
      const url = URL.createObjectURL(videoFile);
      videoRef.current.src = url;
      
      videoRef.current.onloadedmetadata = () => {
        const duration = videoRef.current.duration;
        setVideoDuration(duration);
        setEndTime(Math.min(30, duration));
      };
      
      return () => URL.revokeObjectURL(url);
    }
  }, [videoFile]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.currentTime = startTime;
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleStartChange = (e) => {
    const value = parseFloat(e.target.value);
    if (value < endTime - 1) {
      setStartTime(value);
      if (videoRef.current) {
        videoRef.current.currentTime = value;
      }
    }
  };

  const handleEndChange = (e) => {
    const value = parseFloat(e.target.value);
    if (value > startTime + 1 && value <= videoDuration) {
      setEndTime(value);
    }
  };

  const handleConfirm = () => {
    // Pass the original file with trim parameters to parent
    // Backend will handle the trimming
    onTrimComplete({
      file: videoFile,
      startTime,
      endTime,
      duration: endTime - startTime
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="video-trimmer">
      <div className="trimmer-header">
        <h4>Découper la vidéo</h4>
        <p>Sélectionnez les 30 secondes à publier</p>
      </div>

      <div className="video-preview">
        <video
          ref={videoRef}
          className="trimmer-video"
          onTimeUpdate={() => {
            if (videoRef.current && videoRef.current.currentTime >= endTime) {
              videoRef.current.pause();
              setIsPlaying(false);
            }
          }}
        />
        
        <button className="play-pause-btn" onClick={handlePlayPause}>
          {isPlaying ? <Pause size={24} /> : <Play size={24} />}
        </button>
      </div>

      <div className="time-info">
        <span>Début: {formatTime(startTime)}</span>
        <span className="duration-badge">
          Durée: {formatTime(endTime - startTime)} / 30s max
        </span>
        <span>Fin: {formatTime(endTime)}</span>
      </div>

      <div className="trim-controls">
        <div className="range-container">
          <label>Début</label>
          <input
            type="range"
            min={0}
            max={videoDuration - 1}
            step={0.1}
            value={startTime}
            onChange={handleStartChange}
            className="time-slider start-slider"
          />
        </div>

        <div className="range-container">
          <label>Fin</label>
          <input
            type="range"
            min={1}
            max={videoDuration}
            step={0.1}
            value={endTime}
            onChange={handleEndChange}
            className="time-slider end-slider"
          />
        </div>

        <div className="timeline-preview">
          <div 
            className="selection-bar"
            style={{
              left: `${(startTime / videoDuration) * 100}%`,
              width: `${((endTime - startTime) / videoDuration) * 100}%`
            }}
          />
        </div>
      </div>

      <div className="trimmer-actions">
        <button className="btn-secondary" onClick={onCancel}>
          Annuler
        </button>
        <button 
          className="btn-primary"
          onClick={handleConfirm}
          disabled={endTime - startTime > 30}
        >
          <Check size={18} />
          Confirmer ({formatTime(endTime - startTime)})
        </button>
      </div>
    </div>
  );
};

export default VideoTrimmer;
